/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import EventCondition from 'cio/app-models/conditions/event';

interface ConditionEditorEventInverseArgs {
  condition: EventCondition;
  disabled: boolean;
  allowForeignEvent: boolean;
  disabledOptions?: string[];
  hiddenOptions?: string[];
  size?: string;
  fluid: boolean;
}

export default class ConditionEditorEventInverse extends Component<ConditionEditorEventInverseArgs> {
  initialInverseOption = this.selectedValue;

  get disabledOptions() {
    return this.args.disabledOptions ?? [];
  }

  get hiddenOptions() {
    let hiddenOptions = this.args.hiddenOptions != null ? [...this.args.hiddenOptions] : [];

    if (this.args.allowForeignEvent) {
      // Always hide the "true" option in foreign_event scenarios.
      // Once we verify that no customers have enabled this option, we can
      // remove it entirely from "inverseOptions"
      hiddenOptions.push('true');
      hiddenOptions = hiddenOptions.uniq();
    }

    return hiddenOptions;
  }

  @computed('args.condition.{isForeignEvent,inverse}')
  get selectedValue() {
    if (this.args.condition.isForeignEvent) {
      return 'foreign_event';
    }

    return String(this.args.condition.inverse);
  }

  get inverseOptions() {
    let { allowForeignEvent } = this.args;
    let { disabledOptions } = this;

    let options = [];

    if (allowForeignEvent) {
      options = [
        { value: 'foreign_event', label: 'is performed', disabled: false },
        { value: 'false', label: 'has ever been performed', disabled: false },
        {
          value: 'true',
          label: 'has not been performed before waiting',
          disabled: true,
        }, // this option probably shouldn't have been added in the first place,
        // but we need to keep it in case a customer has already used this
        // (at least until we migrate any broken actions)
      ];
    } else {
      options = [
        { value: 'false', label: 'has been performed', disabled: false },
        { value: 'true', label: 'has not been performed', disabled: false },
      ];
    }

    options = options.map((o) => ({
      ...o,
      disabled: disabledOptions.includes(o.value) && o.value !== this.initialInverseOption,
    }));

    options = options.filter((o) =>
      o.value === this.initialInverseOption ? true : !this.hiddenOptions.includes(o.value),
    );

    if (this.initialInverseOption === 'foreign_event' && !allowForeignEvent) {
      options.unshift({ value: 'foreign_event', label: 'is performed', disabled: true });
    }

    return options;
  }

  @action
  onChange(value: string) {
    let { condition } = this.args;

    if (value === 'foreign_event') {
      condition.setProperties({ isForeignEvent: true, inverse: false });
    } else {
      condition.setProperties({ isForeignEvent: false, inverse: value === 'true', filters: [] });
    }
  }
}
